import { formatMoney as formatMoneyIn, type Moneda } from '@/shared/billing/currency';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AccessDenied,
  ActionMenu,
  Button,
  ConfirmModal,
  Input,
  ModalShell,
  type ActionMenuItem,
} from '@/presentation/components/common';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { CancelPanelPagoAction } from '@/application/actions';
import {
  ClinicInvoiceModal,
  type ClinicInvoiceDTO,
} from '@/presentation/components/billing/ClinicInvoiceModal';
import { ViewPlanDetailModal } from '@/presentation/components/billing/ViewPlanDetailModal';
import { RegisterPaymentModal } from '@/presentation/components/tenants/RegisterPaymentModal';
import {
  ContactDebtorModal,
  type ContactTarget,
} from '@/presentation/components/collections/ContactDebtorModal';
import {
  CONTACT_STATUS_COLORS,
  fetchLatestContacts,
  type LastContactMap,
} from '@/presentation/components/collections/contactHistoryUtils';
import { usePlanCatalog } from '@/presentation/hooks/usePlanCatalog';
import type { PlanCatalogItem } from '@/domain/types/planCatalog';
import type { TenantGridDTO } from '@/domain/repositories/ITenantRepository';
import { buildPrintHtml, formatPeriodLabel } from '@/presentation/components/billing/ClinicInvoiceDocument';
import { htmlToPdfBlob, invoicePdfFilename, saveBlob } from '@/shared/pdf/downloadHtmlPdf';

export type ClinicInvoiceListItem = {
  tenantId: string;
  nombre: string;
  estado: string;
  telefono: string | null;
  email: string | null;
  planName: string;
  planPrice: number;
  extraConsultorios: number;
  extraProfesionales: number;
  whatsappUsed: number;
  whatsappContracted: number;
  whatsappUsedPct: number;
  total: number;
  previousDebt: number;
  amountDue: number;
  isPreview: boolean;
  paymentStatus: string | null;
  paidAt: string | null;
  /** Id de la factura confirmada; null si es borrador o sugerida. */
  facturaId: number | null;
  fechaPago: string | null;
  montoPagado: number | null;
  medioPago: string | null;
  dueDate: string | null;
  planId: number | null;
  invoiceStatus: 'suggested' | 'draft' | 'confirmed';
  moneda: Moneda;
};

type ListResponse = {
  period: string;
  dueDate: string | null;
  items: ClinicInvoiceListItem[];
};

const PAID_STATUSES = ['abonado', 'abonado con mora', 'bonificado', 'gratis'];
const BONUS_STATUSES = ['bonificado', 'gratis'];

function hasPayment(row: ClinicInvoiceListItem): boolean {
  return !!row.paymentStatus && PAID_STATUSES.includes(row.paymentStatus.trim().toLowerCase());
}

/** Estado de la factura en una sola escala: sin confirmar → por cobrar → vencida → cobrada. */
type RowStatus = 'unconfirmed' | 'toCollect' | 'overdue' | 'paid';
type StatusFilter = 'all' | RowStatus;
const STATUS_FILTERS: StatusFilter[] = ['all', 'unconfirmed', 'toCollect', 'overdue', 'paid'];

function rowStatus(row: ClinicInvoiceListItem): RowStatus {
  if (row.invoiceStatus !== 'confirmed') return 'unconfirmed';
  if (row.paidAt || hasPayment(row)) return 'paid';
  if ((row.paymentStatus || '').trim().toLowerCase() === 'mora') return 'overdue';
  return 'toCollect';
}

function isBonus(row: ClinicInvoiceListItem): boolean {
  return BONUS_STATUSES.includes((row.paymentStatus || '').trim().toLowerCase());
}

const STATUS_BADGE_CLASS: Record<RowStatus, string> = {
  unconfirmed: 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
  toCollect: 'bg-slate-100 text-slate-700 dark:bg-gray-700 dark:text-slate-200',
  overdue: 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200',
  paid: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200',
};

/** La grilla de pagos solo necesita id y nombre de la clínica. */
function rowToTenant(row: ClinicInvoiceListItem): TenantGridDTO {
  return {
    id: row.tenantId,
    nombre: row.nombre,
    emailAdministrador: row.email || '',
    telefonoContacto: row.telefono,
    activo: true,
    fechaActivoUTC: new Date().toISOString(),
    montoPeriodoPago: row.total,
    planAdminId: row.planId ?? undefined,
  };
}

function previousPeriod(): string {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Mes vencido: el último período facturable es el mes que terminó. El mes en curso no se factura. */
function lastBillablePeriod(): string {
  return previousPeriod();
}

function shiftPeriod(period: string, delta: number): string {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return previousPeriod();
  const d = new Date(year, month - 1 + delta, 1);
  const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  const max = lastBillablePeriod();
  return next > max ? max : next;
}

function formatMoney(value: number, moneda: Moneda): string {
  return formatMoneyIn(value, moneda, { decimals: false });
}

function isActiveClinic(estado: string): boolean {
  const value = estado.trim().toLowerCase();
  return value === 'active' || value === 'trial';
}

function periodChoices(locale: string, currentMonthLabel: string): { value: string; label: string }[] {
  const current = lastBillablePeriod();
  const now = new Date();
  const choices: { value: string; label: string }[] = [];
  for (let i = 24; i >= 1; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const raw = formatPeriodLabel(value, locale);
    choices.push({
      value,
      label: value === current ? `${raw} (${currentMonthLabel})` : raw,
    });
  }
  return choices;
}

type ConfirmAllResult = {
  period: string;
  confirmed: Array<{ tenantId: string; nombre: string; amountDue: number; edited: boolean; created: boolean }>;
  inactive: Array<{ tenantId: string; nombre: string }>;
  failed: Array<{ tenantId: string; nombre: string; message: string }>;
};

export const ClinicInvoicesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { canAdminister, canOperateBilling } = useAuth();
  const { error: showError, success } = useToast();
  const [searchParams] = useSearchParams();
  // Período en cobranza: por defecto el último mes cerrado (facturación a mes vencido). ?periodo=YYYY-MM lo fija.
  const [period, setPeriod] = useState(() => {
    const fromUrl = searchParams.get('periodo') ?? '';
    return /^\d{4}-\d{2}$/.test(fromUrl) && fromUrl <= lastBillablePeriod() ? fromUrl : previousPeriod();
  });
  const [clinicFilter, setClinicFilter] = useState<'active' | 'all'>('active');
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => {
    const fromUrl = searchParams.get('estado') as StatusFilter | null;
    return fromUrl && STATUS_FILTERS.includes(fromUrl) ? fromUrl : 'all';
  });
  const { plans } = usePlanCatalog();
  const [planDetail, setPlanDetail] = useState<PlanCatalogItem | null>(null);
  const [paymentTenant, setPaymentTenant] = useState<TenantGridDTO | null>(null);
  const [contactTarget, setContactTarget] = useState<ContactTarget | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ClinicInvoiceListItem | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [lastContacts, setLastContacts] = useState<LastContactMap>({});
  const [items, setItems] = useState<ClinicInvoiceListItem[]>([]);
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [exporting, setExporting] = useState<{ current: number; total: number } | null>(null);
  const [target, setTarget] = useState<{ tenantId: string; nombre: string } | null>(null);
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [confirmAllResult, setConfirmAllResult] = useState<ConfirmAllResult | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const canView = canAdminister || canOperateBilling;

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await httpClient.get<ListResponse>('/panel-pagos/facturas', {
        params: { periodo: period },
      });
      setItems(res.data.items ?? []);
      setDueDate(res.data.dueDate ?? null);
    } catch {
      showError(t('pages.clinicInvoices.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [period, showError, t]);

  useEffect(() => {
    setSelected(new Set());
  }, [period]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((row) => {
      if (clinicFilter === 'active' && !isActiveClinic(row.estado)) return false;
      if (statusFilter !== 'all' && rowStatus(row) !== statusFilter) return false;
      if (!q) return true;
      return row.nombre.toLowerCase().includes(q) || row.planName.toLowerCase().includes(q);
    });
  }, [items, search, clinicFilter, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts: Record<StatusFilter, number> = { all: 0, unconfirmed: 0, toCollect: 0, overdue: 0, paid: 0 };
    for (const row of items) {
      if (clinicFilter === 'active' && !isActiveClinic(row.estado)) continue;
      counts.all += 1;
      counts[rowStatus(row)] += 1;
    }
    return counts;
  }, [items, clinicFilter]);

  // Último contacto de cobranza de las facturas impagas (chip en la fila).
  useEffect(() => {
    const ids = items
      .filter((row) => row.invoiceStatus === 'confirmed' && rowStatus(row) !== 'paid')
      .map((row) => row.tenantId);
    if (ids.length === 0) {
      setLastContacts({});
      return;
    }
    void fetchLatestContacts(ids).then(setLastContacts);
  }, [items]);

  const planById = useMemo(() => {
    const map = new Map<number, PlanCatalogItem>();
    for (const plan of plans) map.set(plan.id, plan);
    return map;
  }, [plans]);

  const formatDate = useCallback(
    (iso: string | null) =>
      iso
        ? new Date(iso).toLocaleDateString(i18n.language, { day: '2-digit', month: '2-digit', year: '2-digit' })
        : '',
    [i18n.language]
  );

  const openContact = (row: ClinicInvoiceListItem) => {
    setContactTarget({
      tenantId: row.tenantId,
      clinicaId: null,
      nombreClinica: row.nombre,
      nombreContacto: null,
      planName: row.planName,
      emailAdministrador: row.email,
      telefonoContacto: row.telefono,
      periodo: period,
      totalAdeudado: row.amountDue,
      moneda: row.moneda,
    });
  };

  const handleCancelPayment = async () => {
    if (!cancelTarget?.facturaId || cancelling) return;
    setCancelling(true);
    try {
      const action = container.get<CancelPanelPagoAction>(TYPES.CancelPanelPagoAction);
      await action.execute(cancelTarget.facturaId);
      success(t('pages.billing.cancelPaymentSuccess'));
      setCancelTarget(null);
      await load();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setCancelling(false);
    }
  };

  const rowActions = (row: ClinicInvoiceListItem): ActionMenuItem[] => {
    const status = rowStatus(row);
    const plan = row.planId != null ? planById.get(row.planId) ?? null : null;
    const actions: ActionMenuItem[] = [
      {
        icon: '📄',
        label: t('pages.clinicInvoices.generate'),
        onClick: () => setTarget({ tenantId: row.tenantId, nombre: row.nombre }),
      },
      {
        icon: '📋',
        label: t('pages.billing.planDetail'),
        onClick: () => plan && setPlanDetail(plan),
        disabled: !plan,
        disabledReason: t('pages.billing.planUnknown'),
      },
    ];
    if (status === 'toCollect' || status === 'overdue') {
      actions.push(
        {
          icon: '💵',
          label: t('pages.tenants.registerPayment'),
          onClick: () => setPaymentTenant(rowToTenant(row)),
          separator: true,
        },
        {
          icon: '✉️',
          label: t('pages.collections.contact.action'),
          onClick: () => openContact(row),
        }
      );
    }
    if (status === 'paid') {
      actions.push({
        icon: '↩️',
        label: t('pages.clinicInvoices.cancelPaymentAction'),
        onClick: () => setCancelTarget(row),
        disabled: !row.facturaId || !row.fechaPago || isBonus(row),
        disabledReason: t('pages.billing.cancelPaymentDisabled'),
        separator: true,
      });
    }
    return actions;
  };

  const statusLabel = (row: ClinicInvoiceListItem): string => {
    const status = rowStatus(row);
    if (status === 'unconfirmed') {
      return row.invoiceStatus === 'draft'
        ? t('pages.clinicInvoices.draftBadge')
        : t('pages.clinicInvoices.suggestedBadge');
    }
    if (status === 'paid' && isBonus(row)) return t('pages.clinicInvoices.bonusBadge');
    return t(`pages.clinicInvoices.statusFilter.${status}`);
  };

  const selectableRows = useMemo(
    () => filtered.filter((row) => row.invoiceStatus !== 'suggested'),
    [filtered]
  );
  const selectedIds = useMemo(
    () => selectableRows.filter((row) => selected.has(row.tenantId)).map((row) => row.tenantId),
    [selectableRows, selected]
  );
  const selectedPaidIds = useMemo(
    () => selectableRows.filter((row) => selected.has(row.tenantId) && hasPayment(row)).map((row) => row.tenantId),
    [selectableRows, selected]
  );
  const selectedUnpaidIds = useMemo(
    () => selectedIds.filter((id) => !selectedPaidIds.includes(id)),
    [selectedIds, selectedPaidIds]
  );
  const allSelected = selectableRows.length > 0 && selectedIds.length === selectableRows.length;

  const toggleRow = (tenantId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tenantId)) next.delete(tenantId);
      else next.add(tenantId);
      return next;
    });

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(selectableRows.map((row) => row.tenantId)));

  const handleDeleteSelected = async (ids: string[] = selectedIds) => {
    if (deleting || ids.length === 0) return;
    setDeleting(true);
    try {
      const res = await httpClient.post<{ deleted: number }>('/panel-pagos/facturas/eliminar', {
        tenantIds: ids,
        periodo: period,
      });
      success(t('pages.clinicInvoices.deleteDone', { count: res.data.deleted }));
      setSelected(new Set());
      setConfirmDelete(false);
      await load();
    } catch {
      showError(t('pages.clinicInvoices.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const confirmedRows = useMemo(
    () =>
      items.filter((row) => {
        if (clinicFilter === 'active' && !isActiveClinic(row.estado)) return false;
        return row.invoiceStatus === 'confirmed';
      }),
    [items, clinicFilter]
  );

  /** Facturas sin confirmar de clínicas activas (borradores, editadas o sin guardar): las que confirma "Confirmar pendientes". */
  const pendingRows = useMemo(
    () => items.filter((row) => row.invoiceStatus !== 'confirmed' && isActiveClinic(row.estado)),
    [items]
  );

  const handleConfirmAll = async () => {
    setConfirmingAll(true);
    try {
      const res = await httpClient.post<ConfirmAllResult>('/panel-pagos/facturas/confirmar-todas', {
        periodo: period,
      });
      setConfirmAllOpen(false);
      setConfirmAllResult(res.data);
      if (res.data.confirmed.length > 0) {
        success(t('pages.clinicInvoices.confirmAllDone', { count: res.data.confirmed.length }));
      } else {
        showError(t('pages.clinicInvoices.confirmAllNone'));
      }
      await load();
    } catch (err) {
      const message = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
      showError(typeof message === 'string' && message ? message : t('pages.clinicInvoices.confirmAllError'));
      setConfirmAllOpen(false);
    } finally {
      setConfirmingAll(false);
    }
  };

  const periodOptions = useMemo(
    () => periodChoices(i18n.language, t('pages.clinicInvoices.currentMonth')),
    [i18n.language, t]
  );

  const handleDownloadConfirmed = async () => {
    if (exporting || confirmedRows.length === 0) {
      showError(t('pages.clinicInvoices.downloadConfirmedNone'));
      return;
    }
    setExporting({ current: 0, total: confirmedRows.length });
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      let ok = 0;
      for (let i = 0; i < confirmedRows.length; i += 1) {
        const row = confirmedRows[i];
        setExporting({ current: i + 1, total: confirmedRows.length });
        try {
          const res = await httpClient.get<ClinicInvoiceDTO>(
            `/panel-pagos/factura/${encodeURIComponent(row.tenantId)}`,
            { params: { periodo: period } }
          );
          if (res.data.status !== 'confirmed') continue;
          const blob = await htmlToPdfBlob(buildPrintHtml(res.data, i18n.language, t));
          zip.file(invoicePdfFilename(res.data.nombre, res.data.period), blob);
          ok += 1;
        } catch {
          // sigue con el resto
        }
      }
      if (ok === 0) {
        showError(t('pages.clinicInvoices.downloadConfirmedNone'));
        return;
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveBlob(zipBlob, `Facturas-${period}.zip`);
      if (ok < confirmedRows.length) {
        success(t('pages.clinicInvoices.downloadConfirmedPartial', { ok, total: confirmedRows.length, fail: confirmedRows.length - ok }));
      } else {
        success(t('pages.clinicInvoices.downloadConfirmedDone', { count: ok }));
      }
    } catch {
      showError(t('pages.clinicInvoices.exportError'));
    } finally {
      setExporting(null);
    }
  };

  if (!canView) {
    return <AccessDenied />;
  }

  return (
    <div className="panel-page">
      <div className="panel-page-header">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t('pages.clinicInvoices.title')}</h1>
          <p className="panel-page-subtitle hidden sm:block">
            {t('pages.clinicInvoices.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[12rem]">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('pages.clinicInvoices.period')}
            </label>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                aria-label={t('pages.clinicInvoices.periodPrev')}
                onClick={() => setPeriod((p) => shiftPeriod(p, -1))}
              >
                ‹
              </Button>
              <select
                value={period}
                onChange={(e) => {
                  const next = e.target.value;
                  if (!/^\d{4}-\d{2}$/.test(next)) return;
                  setPeriod(next > lastBillablePeriod() ? lastBillablePeriod() : next);
                }}
                aria-label={t('pages.clinicInvoices.period')}
                className="h-8 min-w-[14rem] rounded-md border border-gray-300 bg-white px-2 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                {periodOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                aria-label={t('pages.clinicInvoices.periodNext')}
                disabled={period >= lastBillablePeriod()}
                onClick={() => setPeriod((p) => shiftPeriod(p, 1))}
              >
                ›
              </Button>
            </div>
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              {t('pages.clinicInvoices.periodHint')}
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => void load()} disabled={isLoading}>
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-3">
          <Input
            label={t('pages.clinicInvoices.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('pages.clinicInvoices.searchPlaceholder')}
          />
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('pages.clinicInvoices.clinicFilter')}
            </span>
            <select
              value={clinicFilter}
              onChange={(e) => setClinicFilter(e.target.value === 'all' ? 'all' : 'active')}
              className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="active">{t('pages.clinicInvoices.filterActive')}</option>
              <option value="all">{t('pages.clinicInvoices.filterAll')}</option>
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('pages.clinicInvoices.statusFilter.label')}
            </span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              {STATUS_FILTERS.map((value) => (
                <option key={value} value={value}>
                  {t(`pages.clinicInvoices.statusFilter.${value}`)} ({statusCounts[value]})
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {dueDate ? (
            <p className="text-sm text-slate-500">
              {t('pages.clinicInvoices.dueDate')}: {new Date(dueDate).toLocaleDateString()}
            </p>
          ) : null}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setConfirmAllOpen(true)}
            disabled={isLoading || confirmingAll || pendingRows.length === 0}
            title={pendingRows.length === 0 ? t('pages.clinicInvoices.confirmAllNone') : undefined}
          >
            {t('pages.clinicInvoices.confirmAll', { count: pendingRows.length })}
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => setConfirmDelete(true)}
            disabled={isLoading || deleting || selectedIds.length === 0}
          >
            {t('pages.clinicInvoices.deleteSelected', { count: selectedIds.length })}
          </Button>
          <Button
            size="sm"
            onClick={() => void handleDownloadConfirmed()}
            disabled={isLoading || exporting !== null || confirmedRows.length === 0}
            isLoading={exporting !== null}
          >
            {exporting
              ? t('pages.clinicInvoices.downloadConfirmedProgress', {
                  current: exporting.current,
                  total: exporting.total,
                })
              : t('pages.clinicInvoices.downloadConfirmed', { count: confirmedRows.length })}
          </Button>
        </div>
      </div>

      {confirmAllResult &&
      confirmAllResult.period === period &&
      (confirmAllResult.confirmed.some((r) => r.edited) ||
        confirmAllResult.inactive.length + confirmAllResult.failed.length > 0) ? (
        <div className="panel-card border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-100">
          <div className="flex items-start justify-between gap-3">
            <p className="font-semibold">{t('pages.clinicInvoices.confirmAllSummaryTitle')}</p>
            <button
              type="button"
              className="text-xs font-semibold opacity-80 hover:opacity-100"
              onClick={() => setConfirmAllResult(null)}
            >
              {t('common.close')}
            </button>
          </div>
          {confirmAllResult.confirmed.some((r) => r.edited) ? (
            <p className="mt-2">
              <span className="font-medium">{t('pages.clinicInvoices.confirmAllEditedConfirmed')}</span>{' '}
              {confirmAllResult.confirmed
                .filter((r) => r.edited)
                .map((r) => r.nombre)
                .join(', ')}
            </p>
          ) : null}
          {confirmAllResult.inactive.length > 0 ? (
            <p className="mt-1">
              <span className="font-medium">{t('pages.clinicInvoices.confirmAllInactive')}</span>{' '}
              {confirmAllResult.inactive.map((r) => r.nombre).join(', ')}
            </p>
          ) : null}
          {confirmAllResult.failed.length > 0 ? (
            <p className="mt-1">
              <span className="font-medium">{t('pages.clinicInvoices.confirmAllFailed')}</span>{' '}
              {confirmAllResult.failed.map((r) => `${r.nombre} (${r.message})`).join(', ')}
            </p>
          ) : null}
        </div>
      ) : null}

      <ConfirmModal
        isOpen={confirmAllOpen}
        onClose={() => (confirmingAll ? undefined : setConfirmAllOpen(false))}
        onConfirm={handleConfirmAll}
        title={t('pages.clinicInvoices.confirmAllTitle')}
        message={t('pages.clinicInvoices.confirmAllMessage', {
          count: pendingRows.length,
          period: formatPeriodLabel(period, i18n.language),
        })}
        confirmText={t('pages.clinicInvoices.confirmAllAction')}
        isLoading={confirmingAll}
      />

      <div className="panel-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-slate-400">
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    disabled={selectableRows.length === 0}
                    onChange={toggleAll}
                    aria-label={t('pages.clinicInvoices.selectAll')}
                  />
                </th>
                <th className="px-4 py-3">{t('pages.clinicInvoices.clinic')}</th>
                <th className="px-4 py-3">{t('pages.clinicInvoices.plan')}</th>
                <th className="px-4 py-3 text-right">{t('pages.clinicInvoices.extrasShort')}</th>
                <th className="px-4 py-3 text-right">{t('pages.clinicInvoices.whatsappShort')}</th>
                <th className="px-4 py-3 text-right">{t('pages.clinicInvoices.invoiceTotal')}</th>
                <th className="px-4 py-3">{t('pages.clinicInvoices.status')}</th>
                <th className="px-4 py-3 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    {t('common.loading')}
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    {t('pages.clinicInvoices.empty')}
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.tenantId} className="hover:bg-slate-50/80 dark:hover:bg-gray-900/30">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(row.tenantId)}
                        disabled={row.invoiceStatus === 'suggested'}
                        onChange={() => toggleRow(row.tenantId)}
                        aria-label={`${t('pages.clinicInvoices.selectRow')}: ${row.nombre}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900 dark:text-white">{row.nombre}</p>
                      <p className="text-xs capitalize text-slate-500">{row.estado}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p>{row.planName}</p>
                      <p className="text-xs text-slate-500">{formatMoney(row.planPrice, row.moneda)}</p>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {row.extraConsultorios}c · {row.extraProfesionales}p
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {row.whatsappUsed.toLocaleString()}/{row.whatsappContracted.toLocaleString()}
                      <span className="ml-1 text-xs text-slate-500">
                        ({row.whatsappUsedPct.toFixed(0)}%)
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold">
                      {formatMoney(row.total, row.moneda)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          STATUS_BADGE_CLASS[rowStatus(row)]
                        }`}
                      >
                        {statusLabel(row)}
                      </span>
                      {rowStatus(row) === 'paid' && row.fechaPago && !isBonus(row) ? (
                        <p className="mt-0.5 text-[11px] text-slate-500">
                          {t('pages.clinicInvoices.paidInfo', {
                            date: formatDate(row.fechaPago),
                            method: row.medioPago || '—',
                          })}
                        </p>
                      ) : null}
                      {(rowStatus(row) === 'toCollect' || rowStatus(row) === 'overdue') && row.dueDate ? (
                        <p className="mt-0.5 text-[11px] text-slate-500">
                          {t('pages.clinicInvoices.dueOn', { date: formatDate(row.dueDate) })}
                        </p>
                      ) : null}
                      {row.invoiceStatus === 'confirmed' && !row.paidAt && row.amountDue > row.total ? (
                        <p className="mt-0.5 text-[11px] text-slate-500">
                          {t('pages.clinicInvoices.amountDueWithDebt', { amount: formatMoney(row.amountDue, row.moneda) })}
                        </p>
                      ) : null}
                      {rowStatus(row) !== 'paid' && lastContacts[row.tenantId] ? (
                        <span
                          className={`panel-chip mt-1 ${
                            CONTACT_STATUS_COLORS[lastContacts[row.tenantId].deliveryStatus] ||
                            CONTACT_STATUS_COLORS.pending
                          }`}
                          title={`${t('pages.collections.contact.lastContact')}: ${lastContacts[row.tenantId].channel} · ${lastContacts[row.tenantId].recipient}`}
                        >
                          {t(`pages.collections.contact.status.${lastContacts[row.tenantId].deliveryStatus}`, {
                            defaultValue: lastContacts[row.tenantId].deliveryStatus,
                          })}
                          {' · '}
                          {lastContacts[row.tenantId].channel === 'email' ? 'Email' : 'WhatsApp'}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setTarget({ tenantId: row.tenantId, nombre: row.nombre })}
                        >
                          {t('pages.clinicInvoices.generate')}
                        </Button>
                        <ActionMenu triggerTitle={t('common.actions')} items={rowActions(row)} />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ModalShell
        isOpen={confirmDelete && selectedPaidIds.length > 0}
        onClose={() => setConfirmDelete(false)}
        title={t('pages.clinicInvoices.paidWarnTitle', { count: selectedPaidIds.length })}
        size="md"
        footer={
          <>
            {selectedUnpaidIds.length > 0 ? (
              <Button
                variant="primary"
                onClick={() => void handleDeleteSelected(selectedUnpaidIds)}
                disabled={deleting}
                isLoading={deleting}
                className="w-full sm:w-auto"
              >
                {t('pages.clinicInvoices.deleteOnlyUnpaid', { count: selectedUnpaidIds.length })}
              </Button>
            ) : null}
            <Button
              variant="danger"
              onClick={() => void handleDeleteSelected(selectedIds)}
              disabled={deleting}
              className="w-full sm:w-auto"
            >
              {t('pages.clinicInvoices.deleteAllAnyway', { count: selectedIds.length })}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setConfirmDelete(false)}
              disabled={deleting}
              className="w-full sm:w-auto"
            >
              {t('common.cancel')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {t('pages.clinicInvoices.paidWarnMessage')}
        </p>
        <ul className="mt-3 max-h-48 list-disc space-y-1 overflow-y-auto pl-5 text-sm text-slate-900 dark:text-white">
          {items
            .filter((row) => selectedPaidIds.includes(row.tenantId))
            .map((row) => (
              <li key={row.tenantId}>
                {row.nombre} <span className="text-slate-500">({row.paymentStatus})</span>
              </li>
            ))}
        </ul>
      </ModalShell>

      <ConfirmModal
        isOpen={confirmDelete && selectedPaidIds.length === 0}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => handleDeleteSelected()}
        title={t('pages.clinicInvoices.deleteTitle')}
        message={t('pages.clinicInvoices.deleteMessage', { count: selectedIds.length, period })}
        confirmText={t('common.delete')}
        variant="danger"
        isLoading={deleting}
      />

      {target ? (
        <ClinicInvoiceModal
          tenantId={target.tenantId}
          period={period}
          clinicName={target.nombre}
          onClose={() => setTarget(null)}
          onChanged={() => void load()}
        />
      ) : null}

      <ViewPlanDetailModal isOpen={!!planDetail} plan={planDetail} onClose={() => setPlanDetail(null)} />

      {paymentTenant ? (
        <RegisterPaymentModal
          key={String(paymentTenant.id)}
          isOpen
          tenant={paymentTenant}
          onClose={() => setPaymentTenant(null)}
          onSuccess={() => {
            setPaymentTenant(null);
            void load();
          }}
        />
      ) : null}

      {contactTarget ? (
        <ContactDebtorModal
          isOpen
          debtor={contactTarget}
          onClose={() => setContactTarget(null)}
          onContactSaved={() => {
            const id = contactTarget.tenantId;
            if (id) {
              void fetchLatestContacts([id]).then((map) => setLastContacts((prev) => ({ ...prev, ...map })));
            }
          }}
        />
      ) : null}

      <ConfirmModal
        isOpen={!!cancelTarget}
        onClose={() => (cancelling ? undefined : setCancelTarget(null))}
        onConfirm={handleCancelPayment}
        title={t('pages.clinicInvoices.cancelPaymentAction')}
        message={
          cancelTarget
            ? `${cancelTarget.nombre} · ${formatPeriodLabel(period, i18n.language)}. ${t('pages.clinicInvoices.cancelPaymentHint')} ${t('pages.billing.cancelPaymentConfirm')}`
            : ''
        }
        confirmText={t('pages.clinicInvoices.cancelPaymentAction')}
        variant="danger"
        isLoading={cancelling}
      />
    </div>
  );
};

